import {
  type SidebarDefinition,
  type SidebarEntry,
  type SidebarSnapshot,
  defineSidebar,
  readSidebar,
} from "@/types/sidebar-session";
import type { UserTiny } from "@/types/user";

import {
  type PatientSidebarEntryPayload,
  patientSidebarDefinition,
} from "./patient-sidebar";

export enum TaskSidebarCommand {
  OPEN = "OPEN",
  ACCEPT = "ACCEPT",
  REJECT = "REJECT",
  SYNC_PATIENT = "SYNC_PATIENT",
}

interface TaskSelectionSnapshot {
  task: TaskSidebarPayload | null;
  patient: SidebarEntry<PatientSidebarEntryPayload> | undefined;
  activeKey: string | null;
  provisionalPatientRevision?: number;
}

export interface TaskSidebarPayload {
  taskId: string | null;
  chartSyncPending: boolean;
  selectionVersion: number;
  previousSelection: TaskSelectionSnapshot | null;
}

type TaskSidebarInput =
  | { taskId: string; command?: TaskSidebarCommand.OPEN }
  | {
      command: TaskSidebarCommand.ACCEPT | TaskSidebarCommand.REJECT;
      selectionVersion: number;
    }
  | {
      command: TaskSidebarCommand.SYNC_PATIENT;
      selectionVersion: number;
      patientTiny: UserTiny;
    };

const emptyTask: TaskSidebarPayload = {
  taskId: null,
  chartSyncPending: false,
  selectionVersion: 0,
  previousSelection: null,
};

export const taskSidebarDefinition: SidebarDefinition<
  TaskSidebarInput,
  TaskSidebarPayload
> = defineSidebar<TaskSidebarInput, TaskSidebarPayload>({
  id: "TASK",
  label: "Task",
  order: 40,
  strategy: {
    planOpen(ctx, input) {
      const current = ctx.read(taskSidebarDefinition)?.payload;
      if ("taskId" in input) {
        const write = ctx.write(taskSidebarDefinition, {
          taskId: input.taskId,
          chartSyncPending: true,
          selectionVersion: 0,
          previousSelection: current?.previousSelection ?? {
            task: current ?? null,
            patient: ctx.read(patientSidebarDefinition),
            activeKey: ctx.snapshot.activeKey,
          },
        });
        return {
          upsert: [
            {
              ...write,
              entry: {
                ...write.entry,
                payload: {
                  ...write.entry.payload,
                  selectionVersion: write.entry.revision,
                },
              },
            },
          ],
          select: taskSidebarDefinition.id,
        };
      }
      if (!current || current.selectionVersion !== input.selectionVersion)
        return {};
      if (input.command === TaskSidebarCommand.ACCEPT) {
        return {
          upsert: [
            ctx.write(taskSidebarDefinition, {
              ...current,
              chartSyncPending: false,
              previousSelection: null,
            }),
          ],
        };
      }
      if (input.command === TaskSidebarCommand.SYNC_PATIENT) {
        const patient = ctx.read(patientSidebarDefinition);
        if (
          !current.chartSyncPending ||
          !current.previousSelection ||
          !patient ||
          patient.payload.patientTiny?.id === input.patientTiny.id
        )
          return {};
        const write = ctx.write(patientSidebarDefinition, {
          patientTiny: input.patientTiny,
          selectedEventId: null,
          autoExpandSection: null,
          hideUpcomingEvents: false,
        });
        return {
          upsert: [
            write,
            ctx.write(taskSidebarDefinition, {
              ...current,
              previousSelection: {
                ...current.previousSelection,
                provisionalPatientRevision: write.entry.revision,
              },
            }),
          ],
        };
      }
      const previous = current.previousSelection;
      if (!previous) return {};
      const patient = ctx.read(patientSidebarDefinition);
      const restorePatient =
        previous.patient &&
        previous.provisionalPatientRevision === patient?.revision;
      return {
        upsert: [
          ...(previous.task
            ? [ctx.write(taskSidebarDefinition, previous.task)]
            : []),
          ...(restorePatient
            ? [ctx.write(patientSidebarDefinition, previous.patient!.payload)]
            : []),
        ],
        remove: previous.task ? [] : [taskSidebarDefinition.id],
        ...(ctx.snapshot.activeKey === taskSidebarDefinition.id
          ? { select: previous.activeKey }
          : {}),
      };
    },
  },
});

export function selectTaskSidebar(
  state: Pick<SidebarSnapshot, "entries">,
): TaskSidebarPayload {
  return readSidebar(state, taskSidebarDefinition)?.payload ?? emptyTask;
}
